import { useState, useEffect } from "react";
import { GlassCard } from "@/components/ui/glass-card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Download, RefreshCw, Search, Bug, Info, AlertTriangle, ShieldAlert } from "lucide-react";
import { format } from "date-fns";

interface DeviceLogsProps {
  deviceId: string;
}

interface LogEntry {
  id: string;
  timestamp: string;
  source_type: string;
  event_type: string;
  message: string;
  log_level: string;
  metadata?: any;
  duration_ms?: number;
  status_code?: number;
}

const DeviceLogs = ({ deviceId }: DeviceLogsProps) => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [selectedEventType, setSelectedEventType] = useState("all");

  const logLevelColors = {
    debug: "bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200",
    info: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    warn: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    error: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    critical: "bg-red-600 text-white"
  };

  const logLevelIcons = {
    debug: Bug,
    info: Info,
    warn: AlertTriangle,
    error: ShieldAlert,
    critical: ShieldAlert
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("integration_sync_logs")
        .select("*")
        .eq("device_id", deviceId)
        .order("timestamp", { ascending: false })
        .limit(500);

      if (error) throw error;

      setLogs(data || []);
      setFilteredLogs(data || []);
    } catch (error) {
      console.error("Error fetching device logs:", error);
      setLogs([]);
      setFilteredLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchLogs, 30000);
    return () => clearInterval(interval);
  }, [deviceId]);

  useEffect(() => {
    let filtered = logs;

    if (selectedLevel !== "all") {
      filtered = filtered.filter(log => log.log_level === selectedLevel);
    }

    if (selectedEventType !== "all") {
      filtered = filtered.filter(log => log.event_type === selectedEventType);
    }

    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.event_type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredLogs(filtered);
  }, [logs, selectedLevel, selectedEventType, searchTerm]);

  const exportToCSV = () => {
    const headers = ["Timestamp", "Level", "Event Type", "Message", "Duration (ms)", "Status Code"];
    const rows = filteredLogs.map(log => [
      format(new Date(log.timestamp), "yyyy-MM-dd HH:mm:ss"),
      log.log_level,
      log.event_type,
      log.message,
      log.duration_ms || "",
      log.status_code || ""
    ]);

    const csv = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `device-${deviceId}-logs-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  const uniqueEventTypes = Array.from(new Set(logs.map(log => log.event_type)));

  return (
    <GlassCard variant="default" hover={false} className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold">Device Logs ({filteredLogs.length})</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportToCSV} disabled={filteredLogs.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="flex gap-4 mb-6 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        
        <Select value={selectedLevel} onValueChange={setSelectedLevel}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Log Level" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Levels</SelectItem>
            <SelectItem value="debug">Debug</SelectItem>
            <SelectItem value="info">Info</SelectItem>
            <SelectItem value="warn">Warning</SelectItem>
            <SelectItem value="error">Error</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>

        <Select value={selectedEventType} onValueChange={setSelectedEventType}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Event Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {uniqueEventTypes.map(type => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground">Loading logs...</div>
      ) : filteredLogs.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Info className="h-12 w-12 mx-auto mb-3 text-muted" />
          <p className="text-lg font-medium mb-2">No Logs Found</p>
          <p className="text-sm">
            {logs.length === 0 
              ? "This device hasn't sent any logs yet."
              : "No logs match your current filters."}
          </p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[180px]">Timestamp</TableHead>
                <TableHead className="w-[100px]">Level</TableHead>
                <TableHead className="w-[180px]">Event Type</TableHead>
                <TableHead>Message</TableHead>
                <TableHead className="w-[100px] text-right">Duration</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log) => {
                const LevelIcon = logLevelIcons[log.log_level as keyof typeof logLevelIcons] || Info;
                return (
                  <TableRow key={log.id}>
                    <TableCell className="font-mono text-xs">
                      {format(new Date(log.timestamp), "MMM dd, HH:mm:ss")}
                    </TableCell>
                    <TableCell>
                      <Badge className={logLevelColors[log.log_level as keyof typeof logLevelColors] || "bg-gray-100"}>
                        <LevelIcon className="h-3 w-3 mr-1" />
                        {log.log_level}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{log.event_type}</TableCell>
                    <TableCell>{log.message}</TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {log.duration_ms ? `${log.duration_ms}ms` : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </GlassCard>
  );
};

export default DeviceLogs;
